<script setup lang="ts">
import { computed, inject } from "vue";
import { Undo2, Redo2 } from "@lucide/vue";

import { HISTORY_KEY } from "../keys";
import { useI18n } from "../composables/useI18n";

const props = defineProps<{
  /** Cloud: show collaboration undo toast before applying undo (matches keyboard shortcut). */
  onBeforeUndo?: () => void;
}>();

const history = inject(HISTORY_KEY, null);
const { t } = useI18n();

const canUndo = computed(() => history?.canUndo.value ?? false);
const canRedo = computed(() => history?.canRedo.value ?? false);

function undo(): void {
  if (!history || !canUndo.value) return;
  props.onBeforeUndo?.();
  history.undo();
}

function redo(): void {
  if (!history || !canRedo.value) return;
  history.redo();
}
</script>

<template>
  <div
    v-if="history"
    class="tpl:flex tpl:items-center tpl:gap-0.5"
    role="group"
    :aria-label="`${t.history.undo} / ${t.history.redo}`"
  >
    <button
      type="button"
      class="tpl-history-btn tpl:relative tpl:flex tpl:cursor-pointer tpl:items-center tpl:justify-center tpl:rounded-[var(--tpl-radius-sm)] tpl:border-none tpl:p-2 tpl:text-[var(--tpl-text-muted)] tpl:transition-all tpl:duration-150"
      :disabled="!canUndo"
      :aria-label="t.history.undo"
      :title="t.history.undo"
      @click="undo"
    >
      <Undo2 :size="18" :stroke-width="1.5" />
    </button>
    <button
      type="button"
      class="tpl-history-btn tpl:relative tpl:flex tpl:cursor-pointer tpl:items-center tpl:justify-center tpl:rounded-[var(--tpl-radius-sm)] tpl:border-none tpl:p-2 tpl:text-[var(--tpl-text-muted)] tpl:transition-all tpl:duration-150"
      :disabled="!canRedo"
      :aria-label="t.history.redo"
      :title="t.history.redo"
      @click="redo"
    >
      <Redo2 :size="18" :stroke-width="1.5" />
    </button>
  </div>
</template>

<style scoped>
.tpl-history-btn:hover:not(:disabled) {
  background-color: var(--tpl-bg-hover);
}

.tpl-history-btn:active:not(:disabled) {
  transform: scale(0.92);
}

.tpl-history-btn:disabled {
  cursor: not-allowed;
  opacity: 0.35;
}
</style>
